import { useCallback, useEffect, useState } from 'react';
import { jukeboxRepository } from '../../data/jukeboxRepository';
import { parseJukeboxPedidoError } from '@/shared/lib/jukeboxPedidoError';
import type { FilaJukebox, PedidoIniciadoBroadcast } from '../../domain/entities/FilaJukebox';
import type { Tela } from '@/shared/models/tela.model';
import type { PlaylistItem } from '@/shared/models/playlist.model';

interface ClienteSnapshot {
  token: string;
}

function isErroDuplicataFila(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && 'code' in error
    && (error as { code: string }).code === '23505';
}

/**
 * usePedidoFlow — envio de pedido + feedback (toast, confirmação, popup de
 * "seu pedido começou a tocar"). Validação de elegibilidade (fila cheia,
 * cooldown, TV offline) e o gate da Batalha de Vibes ficam no composer
 * (`useJukebox`), que orquestra fila/vibe/pedido antes de chamar
 * `inserirPedido` — esse hook só sabe enviar e reagir ao resultado.
 */
export interface PedidoFlowState {
  pedindoId: string | null;
  toast: string | null;
  setToast(msg: string | null): void;
  pedidoConfirmado: { item: PlaylistItem; posicao: number } | null;
  showPopup: boolean;
  inserirPedido(item: PlaylistItem, vibe: string): Promise<void>;
  dismissToast(): void;
  dismissConfirmado(): void;
  dismissPopup(): void;
}

export function usePedidoFlow(
  telaId: string | undefined,
  tela: Tela | null,
  isAuthenticated: boolean,
  cliente: ClienteSnapshot | null | undefined,
  iniciarJanelaFetch: () => number,
  aplicarSnapshotFila: (fila: FilaJukebox[], token: number) => void,
  cancelarJanelaFetch: (token: number) => void,
  pedidoIniciado: PedidoIniciadoBroadcast | null,
  meuSessionId: string,
): PedidoFlowState {
  const [pedindoId, setPedindoId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [pedidoConfirmado, setPedidoConfirmado] = useState<{ item: PlaylistItem; posicao: number } | null>(null);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!pedidoIniciado) return;
    // Se o broadcast não carrega session_id (migration pendente ou pedido legado),
    // exibe o popup para todos — comportamento seguro de fallback.
    const temSessionId = typeof pedidoIniciado.session_id === 'string' && pedidoIniciado.session_id.length > 0;
    const isMeuPedido  = !temSessionId || pedidoIniciado.session_id === meuSessionId;
    if (isMeuPedido) setShowPopup(true);
  }, [pedidoIniciado, meuSessionId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3_500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!pedidoConfirmado) return;
    const t = setTimeout(() => setPedidoConfirmado(null), 6_000);
    return () => clearTimeout(t);
  }, [pedidoConfirmado]);

  const inserirPedido = useCallback(async (item: PlaylistItem, vibe: string) => {
    if (!telaId || !tela) return;
    setPedindoId(item.id);
    try {
      const inserido = await jukeboxRepository.inserirPedido({
        telaId,
        empresaId:        tela.empresa_id,
        youtubeVideoId:   item.video_id,
        titulo:           item.titulo,
        thumbnail:        item.thumbnail,
        sessionId:        meuSessionId,
        vibe,
        duracao_segundos: item.duracao_segundos ?? null,
        clienteToken:     isAuthenticated && cliente ? cliente.token : null,
      });
      // Snapshot passa pela janela de tombstones: um UPDATE→concluido que o
      // CDC aplicou durante o listarAtivos não pode ressuscitar na lista.
      const token = iniciarJanelaFetch();
      try {
        const filaAtualizada = await jukeboxRepository.listarAtivos(telaId);
        aplicarSnapshotFila(filaAtualizada, token);
        const pendentes = filaAtualizada.filter(f => f.status === 'pendente');
        const idx = pendentes.findIndex(f => f.id === inserido.id);
        const posicao = idx >= 0 ? idx + 1 : pendentes.length;
        setPedidoConfirmado({ item, posicao });
      } catch (err) {
        cancelarJanelaFetch(token);
        console.warn('[usePedidoFlow] listarAtivos apos insert:', err);
        // Insert já gravou — confirma com posição otimista; sem toast de erro.
        setPedidoConfirmado({ item, posicao: 1 });
      }
    } catch (error) {
      navigator.vibrate?.([50, 30, 50]);
      const rejeicao = parseJukeboxPedidoError(error);
      if (rejeicao?.motivo === 'ja_na_fila' || isErroDuplicataFila(error)) {
        setToast('Esta música já está na fila!');
      } else if (rejeicao?.motivo === 'limite_sessao') {
        const limite = isAuthenticated ? 5 : 3;
        setToast(`Você já tem ${limite} músicas na fila. Aguarde uma tocar para pedir mais.`);
      } else if (rejeicao?.motivo === 'em_cooldown') {
        setToast('Aguarde um pouco para pedir esta música novamente.');
      } else {
        setToast('Erro ao enviar pedido. Tente novamente.');
      }
    } finally {
      setPedindoId(null);
    }
  }, [telaId, tela, isAuthenticated, cliente, meuSessionId, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch]);

  return {
    pedindoId,
    toast,
    setToast,
    pedidoConfirmado,
    showPopup,
    inserirPedido,
    dismissToast:      () => setToast(null),
    dismissConfirmado: () => setPedidoConfirmado(null),
    dismissPopup:      () => setShowPopup(false),
  };
}
